import React from "react";
import { Grid, Typography } from "material-ui";
import { SignIcon } from "./SignIcon";
import { DownloadIcon } from "./DownloadIcon";
import SwipeableViews from "react-swipeable-views";

import { ArrowRight } from "./ArrowRight";
import { ArrowLeft } from "./ArrowLeft";
import { DocumentDto } from "util/DTOs/DocumentationsDtos";
import { documentDownloadUrl } from "util/request";
import { formatFileName } from "util/utils";
import { downloadAndSaveFile } from "util/platformUtils";

const styles: React.CSSProperties = {
    container: { margin: 0, width: "100%" },
    item: { padding: 0 },
    emptyContainer: { margin: 0, width: "100%", height: "20px" },
    zeroRightPadding: { paddingRight: 0 },
    zeroLeftPadding: { paddingLeft: 0 },
    arrowContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};

export interface ListItem {
    name: string;
    url: string;
}

export enum DocumentListType {
    Signable = "signable",
    Downloadable = "downloadable",
}

interface OwnProps {
    items: DocumentDto[];
    header?: string;
    subheader?: string;
    listType: DocumentListType;
}

interface State {
    currentViewIndex: number;
    viewCount: number;
}

type Props = OwnProps;

const MAX_ITEMS_PER_VIEW = 5;

class DocumentsList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = this.initialState;
    }

    public componentDidMount() {
        const { items } = this.props;
        this.setState({
            viewCount: Math.floor(items.length / MAX_ITEMS_PER_VIEW) + 1,
        });
    }

    private readonly initialState: Readonly<State> = {
        currentViewIndex: 0,
        viewCount: 0,
    };

    public render() {
        const { items, header, subheader, listType } = this.props;
        const isDownloadable = listType === DocumentListType.Downloadable;
        const hasMultipleTabs = items.length > MAX_ITEMS_PER_VIEW;
        return (
            <Grid container style={styles.container}>
                {header ? (
                    <Grid container style={styles.container}>
                        <Grid item xs={12} md={12} style={styles.item}>
                            <h3 className={`docs ${listType}`}>{header}</h3>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container style={styles.emptyContainer}></Grid>
                )}
                <Grid container style={styles.container}>
                    {isDownloadable && (
                        <Typography className={"docs subheader"}>
                            {subheader}
                        </Typography>
                    )}
                    <Grid
                        item
                        xs={12}
                        md={12}
                        className={"docs line blue"}
                    ></Grid>
                    {this.renderDocsList()}
                    {hasMultipleTabs && this.renderBottomButtons()}
                </Grid>
            </Grid>
        );
    }

    private readonly renderDocsList = () => {
        const { items, listType } = this.props;
        const { viewCount, currentViewIndex } = this.state;
        const isDownloadable = listType === DocumentListType.Downloadable;
        const listItems = [...items];
        listItems.forEach(e => {
            e.date = e.changeTime.toString();
            e.date = e.date.slice(0, 10);
        });
        const svArray = Array.from(Array(viewCount), (o, idx) => {
            const itemArray: DocumentDto[] = [];
            for (let i = 0; i < MAX_ITEMS_PER_VIEW; i++) {
                const element = listItems.shift();
                if (element !== undefined) {
                    itemArray.push(element);
                }
            }
            return itemArray;
        });
        return (
            <SwipeableViews
                style={{ width: "100%" }}
                index={currentViewIndex}
                disabled
            >
                {svArray.map((item, index) => {
                    return (
                        <Grid container key={index} style={styles.container}>
                            {item.map((li, i) => {
                                return (
                                    <Grid
                                        key={`${li.name}${index}`}
                                        container
                                        style={styles.container}
                                        className={"docs striped-background"}
                                    >
                                        <Grid item xs={6} md={6}>
                                            <Typography
                                                className={"docs document-name"}
                                            >
                                                {li.name}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4} md={4}> 
                                            <Typography align="right">
                                                {li.date.substring(8, 10)}.
                                                {li.date.substring(5, 7)}.
                                                {li.date.substring(0, 4)}
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            item
                                            xs={2}
                                            md={2}
                                            className={"docs icon-container"}
                                            onClick={() => {
                                                const url = documentDownloadUrl(li.downloadRequest);

                                                downloadAndSaveFile(url, formatFileName(li.name, li.fileExtension), li.mimeType)
                                                    .then(() => {
                                                        console.log("downloading %s successful", url);
                                                    })
                                                    .catch(console.error);
                                            }}
                                        >
                                            {!isDownloadable ? (
                                                <SignIcon />
                                            ) : (
                                                <DownloadIcon />
                                            )}
                                        </Grid>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    );
                })}
            </SwipeableViews>
        );
    };

    private readonly renderBottomButtons = () => {
        const { currentViewIndex, viewCount } = this.state;
        const lightGrey = "#BDBCBC";
        const darkGrey = "#646363";
        const leftFill = currentViewIndex === 0 ? lightGrey : undefined;
        const rightFill =
            currentViewIndex + 1 === viewCount ? lightGrey : undefined;
        return (
            <Grid container style={styles.container}>
                <Grid
                    item
                    xs={6}
                    md={6}
                    onClick={() => this.handleChangeView(Direction.Left)}
                    className={"bottom-button-left"}
                >
                    <Grid container style={styles.container}>
                        <Grid item xs={4} md={4} style={styles.arrowContainer}>
                            <ArrowLeft fill={leftFill} />
                        </Grid>
                        <Grid
                            item
                            xs={8}
                            md={8}
                            style={styles.zeroLeftPadding}
                            className={"docs bottom-button text container left"}
                        >
                            <Typography
                                style={{
                                    color: leftFill ? leftFill : darkGrey,
                                }}
                                className={"docs bottom-button text left"}
                            >
                                Uudemmat
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={6}
                    md={6}
                    onClick={() => this.handleChangeView(Direction.Right)}
                    className={"bottom-button-right"}
                >
                    <Grid container style={styles.container}>
                        <Grid
                            item
                            xs={8}
                            md={8}
                            style={styles.zeroRightPadding}
                            className={
                                "docs bottom-button text container right"
                            }
                        >
                            <Typography
                                style={{
                                    color: rightFill ? rightFill : darkGrey,
                                }}
                                className={"docs bottom-button text right"}
                            >
                                Vanhemmat
                            </Typography>
                        </Grid>
                        <Grid item xs={4} md={4} style={styles.arrowContainer}>
                            <ArrowRight fill={rightFill} />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    private readonly handleChangeView = (direction: Direction) => {
        const { currentViewIndex, viewCount } = this.state;
        if (direction === Direction.Left) {
            if (currentViewIndex !== 0) {
                this.setState({ currentViewIndex: currentViewIndex - 1 });
            }
        } else {
            if (currentViewIndex + 1 !== viewCount) {
                this.setState({ currentViewIndex: currentViewIndex + 1 });
            }
        }
    };
}

enum Direction {
    Right,
    Left,
}

export default DocumentsList;



// WEBPACK FOOTER //
// ./src/views/documentation/components/DocumentsList.tsx